import { ImageName } from "@mcleod/images";
import { ClickEvent, Label, Panel } from "@mcleod/components";
import {Alignment, Api, DateUtil, DisplayType, ModelRow, StringUtil} from "@mcleod/core";
import { DistanceRateUtil } from "./DistanceRateUtil";
import { AutogenLayoutRateIndexDataSummary } from "./autogen/AutogenLayoutRateIndexDataSummary";
import { RATE_VENDORS } from "./DistanceRateIndexCalculator";

const VENDOR_MCLEOD_EXCHANGE = "E";
const VENDOR_TRANSCORE_WEB = "T";
const VENDOR_INTERNET_TRUCKSTOP = "I";
const VENDOR_FREIGHTWAVES = "F";
const VENDOR_GREENSCREENS = "G";

const RATE_TYPE_SPOT = "S";
const RATE_TYPE_BILLED = "B";
const RATE_TYPE_CONTRACT = "C";
const INTERNAL_SPOT_RATE_TYPE = "N";
const INTERNAL_BILLED_RATE_TYPE = "T";


export class RateIndexDataSummary extends AutogenLayoutRateIndexDataSummary {
    private detailsPanelExpanded: boolean = false;
    private listOfPanels: Panel[];
    private distanceUtil = new DistanceRateUtil();

    override onLoad(): void {
        this.labelScacBuyRateHeader.text = 'SCAC Buy';
        this.labelScacSellRateHeader.text = 'SCAC Sell';

        this.listOfPanels = [
            this.panelMpactBuyTotalRate, this.panelMpactSellTotalRate, this.panelScacBuyTotalRate, this.panelScacSellTotalRate,
            this.panelDATSpotTotalRate, this.panelDATContractTotalRate, this.panelTruckstopPostedTotalRate, this.panelTruckstopPaidTotalRate,
            this.panelGreenscreenNetworkTotalRate, this.panelGreenscreenTargetTotalRate,
            this.panelFreightWavesTotalRate, this.panelMpactBuyExpandable, this.panelScacBuyExpandable, this.panelDATSpotExpandable,
            this.panelTruckstopPostedExpandable, this.panelFreightWavesExpandable, this.panelMpactSellExpandable,
            this.panelScacSellExpandable, this.panelDATContractExpandable, this.panelTruckstopPaidExpandable,
            this.panelGreenscreenNetworkExpandable, this.panelGreenscreenTargetExpandable];
    }

    public populateRateIndexResultData(vendorsData: any, showAllRates: boolean, scacCode: string, profile: string) {
        const vendor = vendorsData?.vendor;
        const rateType = vendorsData?.rate_type;

        if ((vendor === VENDOR_MCLEOD_EXCHANGE && rateType === INTERNAL_SPOT_RATE_TYPE) || (vendor === VENDOR_MCLEOD_EXCHANGE && rateType === INTERNAL_BILLED_RATE_TYPE)) {
            this.labelScacBuyRateHeader.text = scacCode + " Buy";
            this.labelScacSellRateHeader.text = scacCode + " Sell";
        }

        const [panelTotalRate, panelExpandable] = this.getPanelPerVendorAndRateType(vendor, rateType);
        if (panelTotalRate === null || panelTotalRate === undefined || panelExpandable === null || panelExpandable === undefined) {
            return;
        }
        panelTotalRate.visible = true;
        panelExpandable.visible = true;

        for (const component of panelExpandable.components) {
            const label = component as Label;
            if (!StringUtil.isEmptyString(label.field)) {
                if (label.field === 'request_date') {
                    const date = new Date(vendorsData[label.field]);
                    label.text = DateUtil.formatDate(date, DateUtil.getUserDateFormat());
                } else if (label.field === 'rate_time_frame') {
                    const timeFrame = vendorsData[label.field];
                    if (timeFrame === 0 || timeFrame === 1) {
                        label.text = timeFrame + " day";
                    } else if (timeFrame > 1) {
                        label.text = timeFrame + " days";
                    }
                } else {
                    label.text = vendorsData[label.field];
                }
                label.color = "subtle.dark";

                // Prevent text wrapping and add ellipsis for origin and destination markets
                if (label.field === 'origin_value' || label.field === 'dest_value') {
                    label.wrap = false;
                    label.style.overflow = "hidden";
                    label.style.textOverflow = "ellipsis";
                    // Set tooltip to show full text on hover if it's being truncated
                    if (!StringUtil.isEmptyString(label.text)) {
                        label.tooltip = label.text;
                    }
                }
            }
            label.tooltipPosition = Alignment.BOTTOM;
            this.distanceUtil.createHoverToDiscover(vendorsData, label);
        }

        panelTotalRate.findComponentByField("total_rate_vendor").forEach((component) => this.updateRateLabel(component as Label, vendorsData));
        panelTotalRate.findComponentByField("total_sell_rate").forEach((component) => this.updateRateLabel(component as Label, vendorsData));
        panelTotalRate.findComponentByField("total_rate_per_mile").forEach((component) => this.updateRateLabel(component as Label, vendorsData));
    }

    private updateRateLabel(label: Label, vendorsData: any) {
        if (!StringUtil.isEmptyString(label.field)) {
            this.distanceUtil.populateValueRatePerMile(label, vendorsData);
            this.distanceUtil.populateValueTotalRateVendor(label, vendorsData);
            this.distanceUtil.createHoverToDiscover(vendorsData, label);
        }
        else {
            if (!StringUtil.isEmptyString(vendorsData?.rate_url)) {
                label.displayType = DisplayType.LINK;
                label.link = vendorsData?.rate_url;
            }
        }
    }

    public hideColumns() {
        const listOfExpandables = this.listOfPanels;
        for (const setData of listOfExpandables) {
            const panel = setData as Panel;
            panel.visible = false;
        }
    }

    buttonFetchOtherResultsOnClick(event: ClickEvent) {
        this.setBusyStatus(true);
        this.buttonFetchOtherResults.imageName = ImageName.lock;
        const lastSearch = this.mainDataSource.lastSearch;
        if (lastSearch?.data) {
            this.fetchAllRates(lastSearch.data["stops"], lastSearch.data["mileage"], lastSearch.data["equipment_id"]);
        }
        else {
            this.fetchAllRates(lastSearch["stops"], lastSearch["mileage"], lastSearch["equipment_id"]);
        }
    }

    setBusyStatus(busy: boolean) {
        if (busy) {
            this.buttonFetchOtherResults.caption = 'Loading ...';
            this.buttonFetchOtherResults.busy = true;
        }
        else {
            this.buttonFetchOtherResults.caption = 'Get Other Rates';
            this.buttonFetchOtherResults.busy = false;
            this.buttonFetchOtherResults.imageName = null;
        }
    }

    buttonExpandDetailsOnClick(event: ClickEvent) {
        const transitionProps = { speed: 100, paddingTop: 0 };
        this.panelExpandedDetails.visible = true;
        if (this.detailsPanelExpanded) this.panelExpandedDetails.collapse(transitionProps).then(() => {
            this.buttonExpandDetails.imageRotation = 90
        }); else this.panelExpandedDetails.expand(transitionProps).then(() => {
            this.buttonExpandDetails.imageRotation = 0
        });
        this.detailsPanelExpanded = !this.detailsPanelExpanded;
    }

    public setDefaultValues() {
        // Reset specific labels explicitly if needed
        this.labelScacBuyRateHeader.text = "SCAC Buy";
        this.labelScacSellRateHeader.text = "SCAC Sell";

        // Iterate through all panels and their components recursively
        this.listOfPanels.forEach((panel: Panel) => {
            this.processPanelComponentsRecursively(panel, (component) => {
                if (component instanceof Label) {
                    this.setDefaultValue(component);
                }
            });
        });

        // Reset specific labels explicitly if needed
        this.labelScacBuyRateHeader.text = "SCAC Buy";
        this.labelScacSellRateHeader.text = "SCAC Sell";
    }

    private processPanelComponentsRecursively(panel: Panel, callback: (component: any) => void) {
        panel.components.forEach((component) => {
            // Apply the callback to the current component
            callback(component);

            // If the component is a Panel, recursively process its children
            if (component instanceof Panel) {
                this.processPanelComponentsRecursively(component, callback);
            }
        });
    }

    setDefaultValue(label: Label) {
        const fieldName = label.field;
        label.displayData(null, null, 0)
        if (!StringUtil.isEmptyString(fieldName)) {
            label.text = "---";
            label.fontSize = "large";
            label.color = "primary";
        }
    }

    public getPanelPerVendorAndRateType(vendor: string, rateType: string) {
        if (vendor === VENDOR_MCLEOD_EXCHANGE && rateType === RATE_TYPE_SPOT)
            return [this.panelMpactBuyTotalRate, this.panelMpactBuyExpandable] as const;
        else if (vendor === VENDOR_MCLEOD_EXCHANGE && rateType === RATE_TYPE_BILLED)
            return [this.panelMpactSellTotalRate, this.panelMpactSellExpandable] as const;
        else if (vendor === VENDOR_MCLEOD_EXCHANGE && rateType === INTERNAL_SPOT_RATE_TYPE)
            return [this.panelScacBuyTotalRate, this.panelScacBuyExpandable] as const;
        else if (vendor === VENDOR_MCLEOD_EXCHANGE && rateType === INTERNAL_BILLED_RATE_TYPE)
            return [this.panelScacSellTotalRate, this.panelScacSellExpandable] as const;
        else if (vendor === VENDOR_TRANSCORE_WEB && rateType === RATE_TYPE_SPOT)
            return [this.panelDATSpotTotalRate, this.panelDATSpotExpandable] as const;
        else if (vendor === VENDOR_TRANSCORE_WEB && rateType === RATE_TYPE_CONTRACT)
            return [this.panelDATContractTotalRate, this.panelDATContractExpandable] as const;
        else if (vendor === VENDOR_INTERNET_TRUCKSTOP && rateType === RATE_TYPE_SPOT)
            return [this.panelTruckstopPostedTotalRate, this.panelTruckstopPostedExpandable] as const;
        else if (vendor === VENDOR_INTERNET_TRUCKSTOP && rateType === RATE_TYPE_BILLED)
            return [this.panelTruckstopPaidTotalRate, this.panelTruckstopPaidExpandable] as const;
        else if (vendor === VENDOR_FREIGHTWAVES && rateType === RATE_TYPE_SPOT)
            return [this.panelFreightWavesTotalRate, this.panelFreightWavesExpandable] as const;
        else if (vendor === VENDOR_GREENSCREENS && rateType === RATE_TYPE_SPOT)
            return [this.panelGreenscreenNetworkTotalRate, this.panelGreenscreenNetworkExpandable] as const;
        else if (vendor === VENDOR_GREENSCREENS && rateType === RATE_TYPE_BILLED)
            return [this.panelGreenscreenTargetTotalRate, this.panelGreenscreenTargetExpandable] as const;
        else
            return [null, null] as const; // Return null if no matching vendor and rate type found
    }

    private async fetchAllRates(stops: any, mileage: any, equipmentId: any) {
        return await Promise.all([
            this.fetchSuggestedPricingRates(stops, mileage, equipmentId, RATE_VENDORS.TRUCKSTOP),
            this.fetchSuggestedPricingRates(stops, mileage, equipmentId, RATE_VENDORS.DAT),
            this.fetchSuggestedPricingRates(stops, mileage, equipmentId, RATE_VENDORS.MPACT),
            this.fetchSuggestedPricingRates(stops, mileage, equipmentId, RATE_VENDORS.FREIGHT_WAVES),
            this.fetchSuggestedPricingRates(stops, mileage, equipmentId, RATE_VENDORS.GREENSCREENS),
        ]).then((response) => {
            this.setBusyStatus(false);
        });
    }

    async fetchSuggestedPricingRates(stops: any, mileage: any, equipmentId: any, vendor: string) {
        return await Api.search("lme/dispatch/rate-index-results-data",
            {
                stops: stops,
                equipment_id: equipmentId,
                show_all_total_rates: true,
                run_type: 3,
                mileage: mileage,
                vendor: vendor
            }).then((response) => {
                const data = response.data[0];
                for (const vendorData of data["vendors_data"]) {
                    this.populateRateIndexResultData(vendorData, true, data["show_company_scac"], null)
                }
            })
    }
}
